import {mapParsedQuestionToFormData} from './mapParsedQuestionToFormData';
import {parseJsonLine} from './parseJsonLine';

/**
 * Parse AI-generated question output (JSON or JSONL format)
 * Returns the first valid question found
 */
export function parseAiGeneratedQuestion(aiOutput: string): {
  questionText: string;
  hint?: string;
  options: string[];
  answer: string;
  explanation: string;
} {
  if (!aiOutput.trim()) {
    throw new Error('AI output is empty');
  }

  // Try to parse as JSONL (multiple lines, each line is a JSON object)
  const lines = aiOutput
    .trim()
    .split('\n')
    .filter((line) => line.trim());

  // Try parsing each line
  const parsedLine = lines.find((line) => {
    const parsed = parseJsonLine(line);
    return parsed && parsed.questionText && parsed.correctAnswer && parsed.explanation;
  });

  if (parsedLine) {
    const parsed = parseJsonLine(parsedLine);
    if (parsed && parsed.questionText && parsed.correctAnswer && parsed.explanation) {
      return mapParsedQuestionToFormData(parsed);
    }
  }

  // Try parsing as single JSON object or array
  try {
    // Remove markdown code blocks if present
    let cleanedOutput = aiOutput.trim();
    const codeBlockMatch = cleanedOutput.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (codeBlockMatch) {
      cleanedOutput = codeBlockMatch[1].trim();
    }

    const parsed = JSON.parse(cleanedOutput);

    // If it's an array, take the first item
    const questionData = Array.isArray(parsed) ? parsed[0] : parsed;

    // Validate the structure matches our expected format
    if (questionData.questionText && questionData.correctAnswer && questionData.explanation) {
      return mapParsedQuestionToFormData(questionData);
    }

    throw new Error('Invalid question format: missing required fields');
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error(`Invalid JSON format: ${error.message}`);
    }
    throw error;
  }
}
